import { forwardRef, useCallback, useEffect, useId, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import DecimalSelect from "./DecimalSelect";

const OVERLAY_OPEN_EVENT = "rating-overlay-open";

type RatingOverlaySelectProps = {
   value?: number;
   max?: number;
   orientation: "horizontal" | "vertical";
   showValidations?: boolean;
   inputClassName?: string;
   ariaLabel?: string;
   tabIndex?: number;
   dataId?: number | string;
   onSelect: (value: number) => void;
   onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
};

const RatingOverlaySelect = forwardRef<HTMLInputElement, RatingOverlaySelectProps>((props, ref) => {
   const [isOpen, setIsOpen] = useState(false);
   const instanceId = useId();
   const max = props.max !== undefined ? Number(props.max) : 10;
   const currentValue = Number(props.value ?? 0);
   const isValid = Number.isFinite(currentValue) && currentValue >= 1 && currentValue <= max;
   const invalid = !!props.showValidations && !isValid;
   const inputClass = props.inputClassName || "checklistRatingInput";

   const close = useCallback(() => {
      setIsOpen(false);
   }, []);

   const open = useCallback(() => {
      document.dispatchEvent(new CustomEvent(OVERLAY_OPEN_EVENT, { detail: instanceId }));
      setIsOpen(true);
   }, [instanceId]);

   useEffect(() => {
      const onOtherOpen = (event: Event) => {
         const detail = (event as CustomEvent<string>).detail;
         if (detail !== instanceId) {
            close();
         }
      };

      document.addEventListener(OVERLAY_OPEN_EVENT, onOtherOpen);
      return () => document.removeEventListener(OVERLAY_OPEN_EVENT, onOtherOpen);
   }, [close, instanceId]);

   useEffect(() => {
      if (!isOpen) {
         return;
      }

      const onKeyDown = (event: globalThis.KeyboardEvent) => {
         if (event.key === "Escape") {
            event.preventDefault();
            close();
         }
      };

      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
   }, [close, isOpen]);

   function handleSelect(value: number) {
      props.onSelect(value);
      close();
   }

   function handleTriggerKeyDown(event: KeyboardEvent<HTMLInputElement>) {
      props.onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (event.key === "Enter" || event.key === " ") {
         event.preventDefault();
         open();
      }
   }

   const displayValue = isValid ? String(currentValue) : "";
   const defaultAria = displayValue ? `Rating ${displayValue}` : "Select rating";

   return (
      <>
         <input
            ref={ref}
            type="text"
            readOnly
            inputMode="numeric"
            autoComplete="off"
            className={`${inputClass} ratingOverlayInput${invalid ? " invalid oops" : ""}${isOpen ? " open" : ""}`}
            value={displayValue}
            tabIndex={props.tabIndex}
            data-id={props.dataId}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={props.ariaLabel || defaultAria}
            onClick={open}
            onKeyDown={handleTriggerKeyDown}
         />
         {isOpen && createPortal(
            <div className="ratingOverlayRoot" role="presentation">
               <button
                  type="button"
                  className="ratingOverlayBackdrop"
                  aria-label="Close rating selection"
                  onClick={close}
               />
               <div
                  className={`ratingOverlayPanel orientation-${props.orientation}`}
                  role="dialog"
                  aria-modal="true"
                  aria-label={`Select rating from 1 to ${max}`}
               >
                  <DecimalSelect
                     value={props.value}
                     max={max}
                     onClickValue={handleSelect}
                     showValidations={false}
                     className={`orientation-${props.orientation}`}
                  />
               </div>
            </div>,
            document.body,
         )}
      </>
   );
});

RatingOverlaySelect.displayName = "RatingOverlaySelect";

export default RatingOverlaySelect;
